import React from 'react';
import { Helmet } from 'react-helmet';
import { useSettings } from '../contexts/SettingsContext';
import { BASE_URL, getFullUrl } from '../utils/constants';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  siteName?: string;
  structuredData?: object;
  canonicalUrl?: string;
  noindex?: boolean;
  nofollow?: boolean;
}

const SEO: React.FC<SEOProps> = ({
  title = 'Sisi ni Tanzania',
  description = 'Join Tanzania\'s Leading Youth Digital Engagement Platform. Connect with thousands of young changemakers, participate in meaningful discussions, attend impactful events, and unlock opportunities for personal and professional growth.',
  keywords = 'Tanzania, youth, platform, engagement, community, events, opportunities, forum, blog, networking, empowerment',
  image,
  url = typeof window !== 'undefined' ? window.location.href : '',
  type = 'website',
  siteName = 'Sisi ni Tanzania',
  structuredData,
  canonicalUrl,
  noindex = false,
  nofollow = false,
}) => {
  const { primaryColor } = useSettings();
  const fullTitle = title.includes('Sisi ni Tanzania') ? title : `${title} - Sisi ni Tanzania`;
  // Use canonicalUrl if provided, otherwise construct from current path
  const canonical = canonicalUrl || (typeof window !== 'undefined' 
    ? getFullUrl(window.location.pathname + window.location.search)
    : BASE_URL);
  const robotsContent = [
    noindex ? 'noindex' : 'index',
    nofollow ? 'nofollow' : 'follow',
  ].join(', ');
  
  // Ensure image URL is absolute when provided
  const ogImage = image
    ? (image.startsWith('http') ? image : `${typeof window !== 'undefined' ? window.location.origin : ''}${image}`)
    : undefined;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content={robotsContent} />
      <meta name="author" content="Sisi ni Tanzania" />
      <link rel="canonical" href={canonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:locale:alternate" content="sw_TZ" />

      {/* Twitter */}
      <meta name="twitter:card" content={ogImage ? "summary_large_image" : "summary"} />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      <meta name="twitter:site" content="@SisiNiTanzania" />

      {/* Additional Meta Tags */}
      <meta name="theme-color" content={primaryColor} />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="Sisi ni Tanzania" />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;

